import { useEffect, useRef } from 'react';
import { usePlaybackStore } from '../stores/playbackStore';
import { useSceneStore } from '../stores/sceneStore';
import { getSpineManager } from '../lib/spine-manager';
import { getPixiApp } from '../lib/pixi-app';

export function useAnimationController() {
  const { isPlaying, speed, fps, currentFrame } = usePlaybackStore();
  const { setCurrentFrame, setTotalFrames, seekToFrame } = usePlaybackStore();
  const { selectedLayerId } = useSceneStore();

  // Ping-pong state (refs to avoid triggering re-renders on every frame)
  const ppTimeRef = useRef(0);
  const ppDirRef  = useRef<1 | -1>(1);

  // Sync play/pause + speed to spine-manager
  useEffect(() => {
    const manager = getSpineManager();
    manager.setTimeScale(isPlaying ? speed : 0);
  }, [isPlaying, speed]);

  // Update total frames: use selected layer when one is picked, otherwise max across all layers
  useEffect(() => {
    if (selectedLayerId) {
      const duration = getSpineManager().getMaxAnimationDuration(selectedLayerId);
      setTotalFrames(Math.round(duration * fps));
    } else {
      const duration = getSpineManager().getMaxSpineDuration();
      setTotalFrames(Math.round(duration * fps)); // 0 → shows "— / —" when no spine layers loaded
    }
  }, [selectedLayerId, fps, setTotalFrames]);

  // Ticker: update current frame display + ping-pong / once logic
  useEffect(() => {
    if (!selectedLayerId || !isPlaying) return;
    const app = getPixiApp();
    if (!app) return;

    // Reset ping-pong state when starting playback
    ppTimeRef.current = 0;
    ppDirRef.current  = 1;

    const fn = () => {
      const manager  = getSpineManager();
      const duration = manager.getMaxAnimationDuration(selectedLayerId);
      if (duration <= 0) return;

      const { playbackMode, loopStart, loopEnd } = usePlaybackStore.getState();
      const total = Math.round(duration * fps);
      if (usePlaybackStore.getState().totalFrames !== total) setTotalFrames(total);

      if (playbackMode === 'ping-pong') {
        // Manual time control: advance by actual elapsed time, bounce at ends
        const dt = (getPixiApp()?.ticker.deltaMS ?? 16.67) / 1000 * speed;
        ppTimeRef.current += dt * ppDirRef.current;

        if (ppTimeRef.current >= duration) {
          ppTimeRef.current = duration;
          ppDirRef.current  = -1;
        } else if (ppTimeRef.current <= 0) {
          ppTimeRef.current = 0;
          ppDirRef.current  = 1;
        }

        manager.seekAllTracks(selectedLayerId, ppTimeRef.current);
        setCurrentFrame(Math.round(ppTimeRef.current * fps));
        return;
      }

      const time = manager.getCurrentTime(selectedLayerId, 0);

      if (playbackMode === 'once') {
        if (time >= duration - 0.02) {
          usePlaybackStore.getState().pause();
          manager.setTimeScale(0);
          return;
        }
      }

      // Default: loop (Spine runtime handles the loop natively)
      setCurrentFrame(Math.round((time % duration) * fps));

      if (loopStart !== null && loopEnd !== null) {
        const loopEndTime   = loopEnd   / fps;
        const loopStartTime = loopStart / fps;
        if (time > loopEndTime) {
          manager.seekAllTracks(selectedLayerId, loopStartTime);
        }
      }
    };

    app.ticker.add(fn);
    return () => { app.ticker.remove(fn); };
  }, [selectedLayerId, isPlaying, speed, fps, setCurrentFrame, setTotalFrames]);

  // When paused and currentFrame changes (step buttons), seek ALL tracks to match
  useEffect(() => {
    if (isPlaying) return;
    const time = currentFrame / fps;
    if (selectedLayerId) {
      getSpineManager().seekAllTracks(selectedLayerId, time);
    } else {
      getSpineManager().seekAllLayersToTime(time);
    }
  }, [currentFrame, isPlaying, selectedLayerId, fps]);

  const handleSeek = (frame: number) => {
    if (!selectedLayerId) return;
    ppTimeRef.current = frame / fps; // keep ping-pong in sync after manual seek
    seekToFrame(frame);
    const time = frame / fps;
    getSpineManager().seekAllTracks(selectedLayerId, time);
  };

  return { handleSeek };
}
